import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TranslationField } from '@shared/interfaces/translation-field.interface';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';

@Pipe({
  name: 'translateField',
  standalone: true,
})
export class TranslateFieldPipe implements PipeTransform {
  private readonly settingsSelector = inject(SettingsStoreSelector);
  private readonly translateService = inject(TranslateService);

  transform(
    translationField: TranslationField | string | undefined,
    options?: {
      defaultTranslationKey?: string;
      params?: Record<string, string | undefined>;
    },
  ): string | undefined {
    const { defaultTranslationKey, params } = options || {};

    if (typeof translationField === 'string') {
      const resolved = translationField.trim();
      if (!resolved) {
        return defaultTranslationKey
          ? this.translateService.instant(defaultTranslationKey, params)
          : undefined;
      }
      return this.interpolate(resolved, params);
    }

    // If no translationField, use defaultTranslationKey with TranslateService
    if (!translationField || Object.keys(translationField).length === 0) {
      if (defaultTranslationKey) {
        return this.translateService.instant(defaultTranslationKey, params);
      }
      return undefined;
    }

    const currentLocale = this.settingsSelector.app.locale;
    const translatedString = translationField[currentLocale];

    if (!translatedString) {
      // Fallback to defaultTranslationKey if translationField has no value
      if (defaultTranslationKey) {
        return this.translateService.instant(defaultTranslationKey, params);
      }
      return undefined;
    }

    // Replace variables in the format {{ variableName }}
    if (params) {
      return this.interpolate(translatedString, params);
    }

    return translatedString;
  }

  private interpolate(
    text: string,
    params: Record<string, string | undefined> | undefined,
  ): string {
    if (!params) {
      return text;
    }
    return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, variableName: string) => {
      return params[variableName] || '';
    });
  }
}
